import { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import { useNavigate } from 'react-router-dom';
import {
  getActiveOwners,
  getGridDataByModuleKey,
  getProductDropdown,
  getProductList,
  getProductReferenceLookupByKey,
} from '../../redux/selector';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDataAndUpdateStore } from '../../redux/actions';
import { updateGridColumnState } from '../../redux/reducer';
import { columnTypes } from '../../shared/initials';
import useProductColumnDefs from './useProductColumnDefs';
import gridOptionsConfig from '../sharedChild/grid/gridOptionsConfig';
import GlobalSearch from '../sharedChild/grid/globalSearch';
import FilterHandler from '../sharedChild/grid/filterHandler';
import useFetchIfNeeded from '../../redux/useFetchIfNeeded';
import { dropdownPayload, productInputData, productReferenceLookup } from '../../shared/constants';
import { isLoadingByKey } from '../../shared/controls/methods/getLoadingState';
import GridSkeleton from '../../shared/component/GridSkeleton';
import LeadsColumns from '../leads/LeadsColumns';
import LeadsFilters, {
  buildAgFilterModel,
  defaultCond,
  condActive,
  ActiveFilterBar,
} from '../leads/LeadsFilters';

const ProductListing = () => {
  const stateData = useSelector((state) => state);
  let storeLoading = isLoadingByKey(stateData, 'productList');
  const navigate = useNavigate();
  const gridRef = useRef();
  const [loading, setLoading] = useState(storeLoading);
  const [gridParams, setGridParams] = useState(null);
  const rowData = useSelector(getProductList);
  const dispatch = useDispatch();
  const gridOptions = gridOptionsConfig('productList', navigate);
  const gridData = useSelector(getGridDataByModuleKey('productList'));
  const owner = useSelector(getActiveOwners);

  useEffect(() => {
    setLoading(storeLoading);
  }, [storeLoading]);

  useFetchIfNeeded(getProductList, fetchDataAndUpdateStore, {
    key: 'productList',
    inputData: productInputData,
  });
  useFetchIfNeeded(getProductDropdown, fetchDataAndUpdateStore, {
    key: 'productDropdown',
    inputData: dropdownPayload,
  });
  useFetchIfNeeded(getProductReferenceLookupByKey, fetchDataAndUpdateStore, {
    key: 'productReferenceLookup',
    inputData: productReferenceLookup,
  });

  const columnDefs = useProductColumnDefs({ gridData, owner });

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      editable: false,
      flex: 1,
      minWidth: 130,
      filter: true,
      resizable: true,
    }),
    []
  );

  const popupParent = useMemo(() => document.body, []);
  const sortingOrder = useMemo(() => ['desc', 'asc', null], []);

  // ---- custom Filters panel (drives AG Grid filterModel = server-side) ----
  const [filterConds, setFilterConds] = useState({});
  const [filterOpen, setFilterOpen] = useState(false);
  const [expandedFilter, setExpandedFilter] = useState(null);
  const filterFields = useMemo(
    () => [
      { field: 'name', label: 'Product Name', type: 'text' },
      { field: 'productCode', label: 'Product Code', type: 'text' },
      { field: 'code', label: 'Code', type: 'text' },
      { field: 'productOwner', label: 'Product Owner', type: 'set', vals: (owner || []).map((x) => x.display_name).filter(Boolean) },
    ],
    [owner]
  );
  const applyFilterConds = useCallback(
    (c) => {
      const api = gridRef.current?.api;
      if (api) api.setFilterModel(buildAgFilterModel(c, filterFields));
    },
    [filterFields]
  );
  const setFilterCond = (field, patch) => {
    setFilterConds((prev) => {
      const meta = filterFields.find((x) => x.field === field);
      const base = prev[field] || defaultCond(meta.type);
      return { ...prev, [field]: { ...base, type: meta.type, ...patch } };
    });
  };
  const removeFilterCond = (field) => {
    setFilterConds((prev) => {
      const n = { ...prev };
      delete n[field];
      applyFilterConds(n);
      return n;
    });
    setExpandedFilter((e) => (e === field ? null : e));
  };
  const clearAllFilters = () => {
    setFilterConds({});
    applyFilterConds({});
    setExpandedFilter(null);
  };
  const hasActiveFilters = Object.values(filterConds).some(condActive);

  // ---- "Columns" show/hide menu ----
  const [colsOpen, setColsOpen] = useState(false);
  const [hiddenCols, setHiddenCols] = useState(() => new Set());
  const columnMenuList = useMemo(
    () =>
      columnDefs
        .filter((c) => c.field && c.headerName)
        .map((c) => ({ id: c.field, label: c.headerName, locked: c.field === 'code' })),
    [columnDefs]
  );
  const syncHiddenCols = useCallback(() => {
    const colApi = gridRef.current?.columnApi;
    if (!colApi?.getColumnState) return;
    setHiddenCols(new Set(colApi.getColumnState().filter((c) => c.hide).map((c) => c.colId)));
  }, []);
  const toggleCol = (id) => {
    const colApi = gridRef.current?.columnApi;
    const api = gridRef.current?.api;
    const makeVisible = hiddenCols.has(id);
    if (colApi?.setColumnsVisible) colApi.setColumnsVisible([id], makeVisible);
    else if (colApi?.setColumnVisible) colApi.setColumnVisible(id, makeVisible);
    else if (api?.setColumnsVisible) api.setColumnsVisible([id], makeVisible);
    syncHiddenCols();
  };
  const toggleAllCols = () => {
    const colApi = gridRef.current?.columnApi;
    const api = gridRef.current?.api;
    const toggleable = columnMenuList.filter((c) => !c.locked).map((c) => c.id);
    if (toggleable.length === 0) return;
    const allShown = toggleable.every((id) => !hiddenCols.has(id));
    const makeVisible = !allShown;
    if (colApi?.setColumnsVisible) colApi.setColumnsVisible(toggleable, makeVisible);
    else if (api?.setColumnsVisible) api.setColumnsVisible(toggleable, makeVisible);
    syncHiddenCols();
  };

  const handleColumnChanged = () => {
    const columnApi = gridRef.current?.columnApi;
    if (!columnApi) return;
    dispatch(
      updateGridColumnState({
        moduleKey: 'productList',
        columnState: columnApi.getColumnState(),
      })
    );
    syncHiddenCols();
  };

  const handleGridReady = (params) => {
    gridRef.current = params;
    setGridParams(params);
    const columnOrderFromStore = gridData?.columnState;
    if (columnOrderFromStore) {
      params.columnApi.applyColumnState({ state: columnOrderFromStore, applyOrder: true });
    }
    syncHiddenCols();
  };

  const onFirstDataRendered = useCallback(() => {
    gridRef.current?.api?.paginationGoToPage();
    syncHiddenCols();
  }, [syncHiddenCols]);

  const filterDefaultSetRef = useRef(null);

  // Server-side filter pipeline: AG Grid filterModel -> filterJson -> fetch.
  const handleFilterChanged = useCallback(
    (event) => {
      if (filterDefaultSetRef.current) {
        filterDefaultSetRef.current = false;
        return;
      }
      const filterModel = event.api.getFilterModel();
      const payload = {
        ...gridData?.inputPayload,
        filterJson: JSON.stringify(filterModel),
        pageNumber: 1,
      };
      dispatch(fetchDataAndUpdateStore({ key: 'productList', inputData: payload }));
    },
    [dispatch, gridData?.inputPayload]
  );

  return (
    <div className={`gos-prod-body${hasActiveFilters ? ' has-filterbar' : ''}`} style={{ width: '100%' }}>
      {/* toolbar: search + result-count + filters/columns */}
      <div className="gos-lead-toolbar">
        <div className="gos-lead-toolbar-left">
          <div className="gos-lead-toolbar-search">
            <GlobalSearch moduleName={'productList'} placeholder="Search products…" />
          </div>
          {gridData?.totalCount !== undefined && (
            <span className="result-count">
              <strong>{Number(gridData.totalCount).toLocaleString()}</strong>{' '}
              {gridData?.inputPayload?.searchText ? 'matches' : 'products'}
            </span>
          )}
        </div>
        <div className="gos-lead-toolbar-right">
          <LeadsFilters
            fields={filterFields}
            conds={filterConds}
            setCond={setFilterCond}
            removeCond={removeFilterCond}
            clearAll={clearAllFilters}
            onApply={applyFilterConds}
            expanded={expandedFilter}
            setExpanded={setExpandedFilter}
            open={filterOpen}
            setOpen={setFilterOpen}
          />
          <LeadsColumns
            columns={columnMenuList}
            hidden={hiddenCols}
            onToggle={toggleCol}
            onToggleAll={toggleAllCols}
            open={colsOpen}
            setOpen={setColsOpen}
          />
        </div>
      </div>

      <ActiveFilterBar
        fields={filterFields}
        conds={filterConds}
        onEdit={(f) => {
          setExpandedFilter(f);
          setFilterOpen(true);
        }}
        onRemove={removeFilterCond}
        onClear={clearAllFilters}
      />

      <div className="cutome_ag_theme">
        {loading && <GridSkeleton rows={10} />}
        <div className={`ag-theme-balham tbl_height`} style={{ width: '100%', height: `100%` }}>
          <AgGridReact
            className="ag-grid-container"
            domLayout="fixedHeight"
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            animateRows={true}
            rowHeight={64}
            headerHeight={44}
            sideBar={false}
            enableRangeSelection={true}
            suppressExcelExport={true}
            suppressRowClickSelection={true}
            pivotPanelShow={'never'}
            rowGroupPanelShow={'never'}
            popupParent={popupParent}
            sortingOrder={sortingOrder}
            onFirstDataRendered={onFirstDataRendered}
            alwaysShowVerticalScroll={true}
            gridOptions={gridOptions}
            columnTypes={columnTypes}
            onFilterChanged={handleFilterChanged}
            onGridReady={handleGridReady}
            onColumnMoved={handleColumnChanged}
            onColumnVisible={handleColumnChanged}
          />
          {gridParams && (
            <FilterHandler
              params={gridParams}
              gridData={gridData}
              columnDefs={columnDefs}
              filterDefaultSetRef={filterDefaultSetRef}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductListing;
